<template>
  <div class="DevPanel">
    <lm-icon-button
      class="Button"
      :class="{'is-selected': show}"
      icon="security"
      @click="show = !show"
    />
    <div v-if="show" class="Panel">
      <div class="Block Version">
        <header>
          <h2>Version</h2>
        </header>
        <p>{{ version }}</p>
      </div>
      <div class="Block Trial">
        <header>
          <h2>Trial</h2>
          <div class="Actions">
            <a
              v-if="!trialActive"
              @click.prevent="$store.dispatch('forceTrialSubscription')"
              >Activate</a
            >
            <a
              v-if="trialActive"
              @click.prevent="$store.dispatch('expireTrialSubscription')"
              >Expire</a
            >
          </div>
        </header>
        <p>{{ resolveTrialState() }}</p>
      </div>
      <div class="Block Startup">
        <header>
          <h2>Landscape Type</h2>
          <div class="Actions">
            <a
              v-if="isStartup !== null"
              @click="$store.dispatch('toggleLandscapeType', true)"
              >Toggle</a
            >
          </div>
        </header>
        <p v-html="showType"></p>
      </div>
      <div class="Block">
        <header>
          <h2>Mentions</h2>
          <div class="Actions">
            <a @click="$store.dispatch('resetAllMentions')">Reset All</a>
          </div>
        </header>
        <p>
          {{ mentions.length }} total mentions -
          {{ mentions.filter(m => !m.read).length }} unread
        </p>
      </div>
      <div class="Block">
        <header>
          <h2>Token</h2>
          <div class="Actions">
            <a class="RenewToken" @click.prevent="renewToken()">Renew</a>
            <a class="RenewToken" @click.prevent="copyToken()">Copy</a>
          </div>
        </header>
        <p>Renewed at {{ tokenRenewedAt | formattedHour }}</p>
        <textarea ref="token" v-model="token" class="Token"></textarea>
      </div>
      <div class="Block">
        <header>
          <h2>User data</h2>
        </header>
        <p v-for="d of userData" :key="d">{{ d }}</p>
      </div>
      <div v-if="currentTopic" class="Block">
        <header>
          <h2>JIRA</h2>
          <div class="Actions">
            <select v-model="selectedJiraProject">
              <option v-for="project in jiraProjects" :key="project">{{
                project
              }}</option>
            </select>
            <select v-model="selectedJiraIssue">
              <option v-for="issue in jiraIssues" :key="issue">{{
                issue
              }}</option>
            </select>
            <a class="RenewToken" @click.prevent="createJiraTicket()"
              >Create Ticket</a
            >
          </div>
        </header>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/lib/api'
import auth from '@/lib/auth'
import axios from 'axios'
import {mapGetters} from 'vuex'
import LmIconButton from '@/components/ui/LmIconButton'
import EventHub from '@/lib/eventHub'
import {Events} from '@/lib/definitions'

export default {
  name: 'DevPanel',
  components: {
    LmIconButton
  },
  props: {},
  data() {
    return {
      show: false,
      token: null,
      tokenRenewedAt: null,
      trialActive: null,
      jiraProjects: ['API', 'APP', 'WEB'],
      jiraIssues: ['Bug', 'Task', 'Analysis'],
      selectedJiraProject: 'API',
      selectedJiraIssue: 'Bug',
      version: '0'
    }
  },
  computed: {
    ...mapGetters([
      'userData',
      'isTrial',
      'isTrialExpired',
      'currentTopic',
      'currentLandscape',
      'isStartup',
      'mentions'
    ]),
    showType() {
      let s = '(Choose a Landscape)'
      if (this.isStartup !== null) {
        s = this.isStartup ? 'Startup' : 'Business'
        s += ` (${this.currentLandscape.name})`
      }
      return s
    }
  },
  watch: {
    show() {
      if (this.show) this.refreshTokenValues()
    }
  },
  mounted() {
    this.version = process.env.VUE_APP_VERSION
  },
  methods: {
    resolveTrialState() {
      let s = ''
      if (this.isTrial) {
        if (this.isTrialExpired) {
          s = 'Trial expired'
          this.trialActive = false
        } else {
          s = 'Trial active'
          this.trialActive = true
        }
      } else {
        s = 'Trial not active'
        this.trialActive = false
      }
      return s
    },
    createJiraTicket() {
      this.$store
        .dispatch('toJira', {
          topicId: this.currentTopic._id,
          jiraProject: this.selectedJiraProject,
          issueType: this.selectedJiraIssue
        })
        .then(url => {
          EventHub.emit(Events.SHOW_SNACKBAR, `Jira ticket created: ${url}`)
        })
    },
    renewToken() {
      auth.renewToken().then(() => this.refreshTokenValues())
    },
    copyToken() {
      this.$refs.token.focus()
      this.$refs.token.setSelectionRange(0, -1)
      document.execCommand('copy')
    },
    refreshTokenValues() {
      this.token = axios.defaults.headers.common['Authorization']
      this.tokenRenewedAt = api.tokenRenewedAt
    }
  }
}
</script>

<style scoped>
.DevPanel {
  position: absolute;
  top: 2px;
  left: 2px;
  z-index: theme('zIndex.devPanel');
  .Button {
    color: #7f7f7f;
    margin: 2px 1px;
    &.is-selected,
    &:hover {
      color: theme('colors.highlight');
    }
  }
  .Panel {
    width: 320px;
    margin: 0;
    background: white;
    border: 1px solid #dadada;
    box-shadow: 7px 4px 20px -12px rgba(0, 0, 0, 0.5);
    .Block {
      padding: 0 10px 10px;
      header {
        display: flex;
        align-items: center;
        margin: 0 -10px 10px;
        padding: 10px 10px;
        border-top: 1px solid #dadada;
        border-bottom: 1px solid #dadada;
        h2 {
          font-size: 12px;
          text-transform: uppercase;
          font-weight: 500;
          color: #333333;
        }
        .Actions {
          margin-left: auto;
          a {
            margin-left: 5px;
            font-size: 13px;
          }
        }
      }
      &:first-child header {
        border-top: none;
      }
      p {
        margin: 0 0 10px;
        font-size: 12px;
        word-wrap: break-word;
        color: #7f7f7f;
      }
      .Token {
        width: 100%;
        height: 50px;
        border: none;
        margin: 0 0 10px;
        font-size: 11px;
        color: #7f7f7f;
      }
    }
  }
}
</style>
